import { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { LayoutDashboard, FileText, BookOpen, MessageSquare, LogOut, ChevronLeft, Menu } from "lucide-react";

const sidebarLinks = [
  { href: "/admin", icon: LayoutDashboard, label: "Dashboard" },
  { href: "/admin/pages", icon: FileText, label: "Pages" },
  { href: "/admin/blogs", icon: BookOpen, label: "Blog Posts" },
  { href: "/admin/contacts", icon: MessageSquare, label: "Contacts" },
];

export default function AdminLayout({ children }) {
  const [collapsed, setCollapsed] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const token = localStorage.getItem("brgt_token");
    if (!token) navigate("/admin/login");
  }, [navigate]);

  const handleLogout = () => {
    localStorage.removeItem("brgt_token");
    localStorage.removeItem("brgt_user");
    navigate("/admin/login");
  };

  return (
    <div className="flex min-h-screen bg-slate-50" data-testid="admin-layout">
      {/* Sidebar */}
      <aside
        data-testid="admin-sidebar"
        className={`fixed left-0 top-0 h-full bg-navy text-white z-40 transition-all duration-300 flex flex-col ${
          collapsed ? "w-16" : "w-60"
        }`}
      >
        <div className="flex items-center justify-between h-16 px-4 border-b border-white/10">
          {!collapsed && (
            <Link to="/" className="flex items-center gap-2">
              <img src="logo.webp" alt="brgt" className="h-8 w-auto rounded" />
              <span className="font-heading font-bold text-sm">brgt.ai CMS</span>
            </Link>
          )}
          <button
            data-testid="sidebar-toggle"
            onClick={() => setCollapsed(!collapsed)}
            className="p-1.5 rounded-lg hover:bg-white/10 transition-colors"
          >
            {collapsed ? <Menu size={18} /> : <ChevronLeft size={18} />}
          </button>
        </div>

        <nav className="flex-1 py-4 px-2 space-y-1">
          {sidebarLinks.map((link) => {
            const Icon = link.icon;
            const isActive = location.pathname === link.href;
            return (
              <Link
                key={link.href}
                to={link.href}
                data-testid={`admin-nav-${link.label.toLowerCase().replace(' ', '-')}`}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all ${
                  isActive ? "bg-white/15 text-amber" : "text-slate-300 hover:bg-white/10 hover:text-white"
                }`}
              >
                <Icon size={18} />
                {!collapsed && <span>{link.label}</span>}
              </Link>
            );
          })}
        </nav>

        <div className="px-2 pb-4">
          <button
            data-testid="admin-logout-btn"
            onClick={handleLogout}
            className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-slate-300 hover:bg-white/10 hover:text-white transition-colors w-full"
          >
            <LogOut size={18} />
            {!collapsed && <span>Logout</span>}
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <div className={`flex-1 transition-all duration-300 ${collapsed ? "ml-16" : "ml-60"}`}>
        <div className="p-6 md:p-8">{children}</div>
      </div>
    </div>
  );
}
